import { useTranslation } from 'react-i18next';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import styles from './UploadModal.module.css';

interface IApprovalTogglesProps {
  canApprove: boolean;
  submitForApproval: boolean;
  autoApprove: boolean;
  disabled: boolean;
  /** The version this document has waiting for a decision, if any. Decisions
   *  run oldest-first, so while one is waiting nothing newer can approve
   *  itself — the box stays visible but off, and says which version to clear. */
  awaitingDecision: number | null;
  onSubmitForApprovalChange: (value: boolean) => void;
  onAutoApproveChange: (value: boolean) => void;
}

/** The upload's approval options: submit-for-approval (with its draft/submit
 *  hint), and — for approvers, on a submitted version only — approve-on-upload
 *  (a draft has nothing to approve). */
export function ApprovalToggles({
  canApprove, submitForApproval, autoApprove, disabled, awaitingDecision,
  onSubmitForApprovalChange, onAutoApproveChange,
}: IApprovalTogglesProps) {
  const { t } = useTranslation();
  const blocked = awaitingDecision !== null;

  return (
    <>
      <div className={styles.field} data-tour="documents-upload-approval">
        <label className={`${styles.approvalToggle} ${disabled ? styles.approvalToggleDisabled : ''}`}>
          <Checkbox
            checked={submitForApproval}
            onCheckedChange={onSubmitForApprovalChange}
            disabled={disabled}
            ariaLabel={t('documents.upload.submitForApproval')}
          />
          <span className={styles.approvalToggleText}>
            <span className={styles.approvalToggleLabel}>{t('documents.upload.submitForApproval')}</span>
            <span className={styles.approvalToggleHint}>
              {t(submitForApproval ? 'documents.upload.submitHint' : 'documents.upload.draftHint')}
            </span>
          </span>
        </label>
      </div>

      {canApprove && submitForApproval && (
        <div className={styles.field}>
          <label className={`${styles.approvalToggle} ${disabled ? styles.approvalToggleDisabled : ''}`}>
            <Checkbox
              checked={autoApprove && !blocked}
              onCheckedChange={onAutoApproveChange}
              disabled={blocked || disabled}
              ariaLabel={t('documents.upload.approveImmediately')}
            />
            <span className={`${styles.approvalToggleText} ${blocked ? styles.approvalToggleBlocked : ''}`}>
              <span className={styles.approvalToggleLabel}>{t('documents.upload.approveImmediately')}</span>
              <span className={styles.approvalToggleHint}>
                {blocked
                  ? t('documents.upload.approveBlockedHint', { version: awaitingDecision })
                  : t('documents.upload.approveImmediatelyHint')}
              </span>
            </span>
          </label>
        </div>
      )}
    </>
  );
}